import React from "react";
import { Loader2, Sparkles, Calendar, TrendingUp } from "lucide-react";

export const FestivalCard = ({ festival, highlight }) => (
  <div
    className={`p-3 rounded-xl border ${highlight ? "border-rose-300 bg-white" : "border-gray-200 bg-gray-50"}`}
    data-testid={`doozyad-festival-${festival.id}`}
  >
    <div className="flex items-center justify-between mb-1">
      <div className="text-sm font-bold text-[#1E1B4B]">{festival.name}</div>
      {festival.days_until !== null && festival.days_until !== undefined && (
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${highlight ? "bg-rose-100 text-rose-700" : "bg-gray-100 text-gray-700"}`}>
          T-{festival.days_until}d
        </span>
      )}
    </div>
    <div className="text-[11px] text-[#64748B]">{festival.next_occurrence}</div>
    <div className="text-[11px] text-[#64748B] mt-1">
      Theme: <span className="font-medium text-[#1E1B4B]">{festival.theme_id}</span> · Lead {festival.lead_days}d
      {festival.annual && <span className="ml-2 text-emerald-600">· annual</span>}
    </div>
  </div>
);

export const TrendsPanel = ({ festivals, googleTrends, onRunFestival, onRunBrainstorm, festivalRunning, trendBrainstorming }) => {
  const dueSoon = festivals.filter((f) => f.is_due_soon);
  const upcoming = festivals.filter((f) => !f.is_due_soon).slice(0, 8);

  return (
    <div className="space-y-5" data-testid="doozyad-trends-panel">
      {/* Manual triggers */}
      <div className="bg-gradient-to-br from-rose-50 to-orange-50 border border-rose-200 rounded-2xl p-5">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-1">
          <Sparkles size={16} className="text-rose-600" /> Trend agents · manual triggers
        </h3>
        <p className="text-xs text-[#64748B] mb-3">
          Festival agent runs daily 8 AM IST and generates a draft 7 days before each event.
          Trend brainstorm runs Mondays 9 AM IST and generates 3 trend-driven drafts via Gemini + Google Trends India.
          Trigger them now to see what's queued.
        </p>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={onRunFestival}
            disabled={festivalRunning}
            className="px-4 py-2 bg-gradient-to-r from-rose-600 to-orange-600 text-white text-xs font-semibold rounded-xl disabled:opacity-50 flex items-center gap-2"
            data-testid="doozyad-run-festival-btn"
          >
            {festivalRunning ? <Loader2 size={13} className="animate-spin" /> : <Calendar size={13} />}
            Run festival pass now
          </button>
          <button
            onClick={onRunBrainstorm}
            disabled={trendBrainstorming}
            className="px-4 py-2 bg-gradient-to-r from-violet-600 to-purple-600 text-white text-xs font-semibold rounded-xl disabled:opacity-50 flex items-center gap-2"
            data-testid="doozyad-run-brainstorm-btn"
          >
            {trendBrainstorming ? <Loader2 size={13} className="animate-spin" /> : <TrendingUp size={13} />}
            Brainstorm 3 trend drafts
          </button>
        </div>
      </div>

      {/* Google Trends snapshot */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5" data-testid="doozyad-google-trends-block">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-3">
          <TrendingUp size={16} className="text-violet-600" /> Google Trends · India · last 7 days
        </h3>
        {!googleTrends ? (
          <div className="flex items-center gap-2 text-xs text-[#64748B]">
            <Loader2 size={13} className="animate-spin" /> Loading…
          </div>
        ) : googleTrends.status === "ok" ? (
          <div>
            <div className="text-xs text-emerald-700 mb-3">
              Top: <b>{googleTrends.top.keyword}</b> · interest score {googleTrends.top.interest}
            </div>
            <div className="space-y-1.5">
              {Object.entries(googleTrends.trends).sort((a, b) => b[1] - a[1]).map(([kw, score]) => (
                <div key={kw} className="flex items-center gap-2 text-xs">
                  <span className="w-44 text-[#1E1B4B] truncate">{kw}</span>
                  <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-violet-500 to-fuchsia-500" style={{ width: `${Math.min(score, 100)}%` }} />
                  </div>
                  <span className="w-10 text-right text-[#64748B] tabular-nums">{score}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Google Trends unavailable (rate-limited or offline). Brainstorm will run without keyword anchor.
          </div>
        )}
      </div>

      {/* Due soon */}
      {dueSoon.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-2xl p-5" data-testid="doozyad-festivals-due-soon">
          <h3 className="text-sm font-bold text-rose-900 flex items-center gap-2 mb-3">
            <Calendar size={16} /> Festivals due in lead window ({dueSoon.length})
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {dueSoon.map((f) => (
              <FestivalCard key={f.id} festival={f} highlight />
            ))}
          </div>
        </div>
      )}

      {/* Upcoming */}
      <div className="bg-white border border-gray-200 rounded-2xl p-5">
        <h3 className="text-sm font-bold text-[#1E1B4B] flex items-center gap-2 mb-3">
          <Calendar size={16} className="text-[#64748B]" /> Upcoming festivals (next 8)
        </h3>
        <div className="grid sm:grid-cols-2 gap-3">
          {upcoming.map((f) => (
            <FestivalCard key={f.id} festival={f} />
          ))}
          {upcoming.length === 0 && (
            <div className="text-xs text-[#64748B]">No upcoming festivals in the calendar.</div>
          )}
        </div>
      </div>
    </div>
  );
};
